import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from "react";
import {
  getHideoutModule,
  moduleLevel,
  RANGE_MODULE_ID,
  type ProfileWire,
  type SkillId,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import { formatMmSs } from "../meta/format.js";
import { ModuleDetail, useTicker } from "../meta/hideout.js";
import {
  projectHideout,
  reasonLabel,
  stashCountsOf,
  trainingRows,
} from "../meta/hideoutLogic.js";
import { useHud, type RangeHud } from "../store.js";
import { FONT, PANEL_BG, PANEL_LINE, TEXT, TEXT_DIM } from "../theme.js";
import {
  GYM_REPS,
  gymScore,
  markerPos01,
  pressGym,
  startGym,
  zoneFor,
  type GymState,
} from "./gymLogic.js";

/**
 * THE HUD OF THE WALKABLE SHELTER: a crosshair, a line saying what Escape
 * does, the range's scoreboard, and whichever overlay the player opened with
 * F — a module's own detail panel or the gym. The station panel renders the
 * tab's own `ModuleDetail` against the same `projectHideout` and `useTicker`,
 * so the two can never disagree about a cost.
 *
 * Escape is NOT this file's: leaving and closing are input-mode changes the
 * game arbitrates (`main.ts`); this only SAYS what Escape means and offers a ×
 * through `closeHideoutOverlay`. The one key it listens for is Space, swallowed
 * so the body does not jump during reps. `HideoutHud` reads the store; the
 * three components below take props, so they render statically.
 */

/** The gold the whole menu uses for anything that is a live number. */
const ACCENT = "rgba(214, 176, 108, 0.9)";
const GOOD = "#8fd39a";
const BAD = "#e08b82";

// ---------------------------------------------------------------------------

/**
 * The shelter's whole HUD, or nothing. Split so the ticker inside `ShelterHud`
 * only runs while the player is down there.
 */
export function HideoutHud(): ReactElement | null {
  const screen = useHud((s) => s.screen);
  if (screen !== "hideout") return null;
  return <ShelterHud />;
}

function ShelterHud(): ReactElement {
  const profile = useHud((s) => s.profile);
  const profileAtMs = useHud((s) => s.profileAtMs);
  const station = useHud((s) => s.hideoutStation);
  const gym = useHud((s) => s.hideoutGym);
  const range = useHud((s) => s.hideoutRange);
  const now = useTicker();

  const overlay = station !== null || gym;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        font: FONT,
        color: TEXT,
        // The layer never takes the mouse — the pointer is LOCKED — and each
        // overlay turns it back on for its own scrim.
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      {overlay ? null : <Crosshair />}

      <Hint>
        {overlay
          ? "Esc — close"
          : range !== null
            ? "Esc — leave the range"
            : "Esc — back to the menu"}
      </Hint>

      {range === null || profile === null ? null : (
        <RangeBoard
          hud={range}
          // The DRILL is the level-2 range's; read off the profile, since
          // `best`
          // is 0 for a player who has not run one.
          drillAvailable={moduleLevel(profile.hideout, RANGE_MODULE_ID) >= 2}
        />
      )}

      {station === null || profile === null ? null : (
        <StationPanel
          profile={profile}
          profileAtMs={profileAtMs}
          moduleId={station}
          nowMs={now}
        />
      )}

      {!gym || profile === null ? null : (
        <GymOverlay profile={profile} profileAtMs={profileAtMs} nowMs={now} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The two things that are always up
// ---------------------------------------------------------------------------

/**
 * A dot. No reticle — nothing to aim at — but the interaction prompt answers
 * "what am I pointing at", and that needs a centre.
 */
function Crosshair(): ReactElement {
  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        width: 4,
        height: 4,
        marginLeft: -2,
        marginTop: -2,
        borderRadius: "50%",
        background: "rgba(240, 244, 248, 0.72)",
        boxShadow: "0 0 2px rgba(0, 0, 0, 0.8)",
        pointerEvents: "none",
      }}
    />
  );
}

/** The one line that says what the one key the player already pressed will do
 *  next. Top right, out of the way of the range's board. */
function Hint(props: { children: ReactNode }): ReactElement {
  return (
    <div
      style={{
        position: "absolute",
        right: 16,
        top: 16,
        fontSize: 11,
        color: TEXT_DIM,
        whiteSpace: "nowrap",
      }}
    >
      {props.children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The range
// ---------------------------------------------------------------------------

export interface RangeBoardProps {
  hud: RangeHud;
  /** Whether the level-2 timed drill exists to be started. */
  drillAvailable: boolean;
}

/**
 * The scoreboard over the lane: hands, shooting, drill. TOP CENTRE, in the same
 * glance as the plates. Every number arrives from the game via `RangeHud`; only
 * the hit rate is computed here.
 */
export function RangeBoard(props: RangeBoardProps): ReactElement {
  const { hud } = props;
  const drilling = hud.drillSecLeft !== null;

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: 16,
        transform: "translateX(-50%)",
        padding: "7px 14px",
        borderRadius: 4,
        background: PANEL_BG,
        border: `1px solid ${PANEL_LINE}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontSize: 11, color: TEXT_DIM, letterSpacing: "0.06em" }}>
        {hud.weaponName}
      </span>

      <span style={{ fontSize: 15 }}>
        Hits {hud.hits} / Shots {hud.shots}
        <span style={{ marginLeft: 10, color: TEXT_DIM, fontSize: 12 }}>
          {hitRate(hud.hits, hud.shots)}
        </span>
      </span>

      {drilling ? (
        <span style={{ fontSize: 13, color: ACCENT }}>
          {/* `drillSecLeft` is non-null exactly here, which the ?? 0 only has to
              satisfy the compiler about. */}
          Drill {formatMmSs((hud.drillSecLeft ?? 0) * 1000)} · Score {hud.drillScore}
        </span>
      ) : hud.best > 0 ? (
        <span style={{ fontSize: 12, color: TEXT_DIM }}>Best drill: {hud.best}</span>
      ) : null}

      {props.drillAvailable && !drilling ? (
        <span style={{ fontSize: 11, color: TEXT_DIM }}>
          <span style={{ opacity: 0.7 }}>F</span> — start the drill
        </span>
      ) : null}
    </div>
  );
}

/** `7 / 12` → `"58 %"`, and an em dash before the first shot: "0 %" is a claim
 *  about somebody who has missed, and nobody has fired yet. */
function hitRate(hits: number, shots: number): string {
  if (!Number.isFinite(shots) || shots <= 0) return "—";
  return `${Math.round((hits / shots) * 100)} %`;
}

// ---------------------------------------------------------------------------
// A station's panel
// ---------------------------------------------------------------------------

export interface StationPanelProps {
  profile: ProfileWire;
  /** `performance.now()` when that profile landed — half of the projection. */
  profileAtMs: number;
  moduleId: string;
  /** The other half: a `performance.now()` reading, ticked once a second. */
  nowMs: number;
}

/**
 * The module the player is standing at, as the tab's detail column over the 3D
 * view. The header names the module a second time on purpose: it is the title
 * bar, holds the way out and stays put while the recipe list scrolls.
 */
export function StationPanel(props: StationPanelProps): ReactElement {
  const h = useMemo(
    () => projectHideout(props.profile.hideout, props.profileAtMs, props.nowMs),
    [props.profile.hideout, props.profileAtMs, props.nowMs],
  );
  const counts = useMemo(() => stashCountsOf(props.profile.stash), [props.profile.stash]);
  const def = getHideoutModule(props.moduleId);

  return (
    <Scrim>
      <Panel title={def?.name ?? "The shelter"}>
        {def === null ? (
          <span style={{ fontSize: 11, color: TEXT_DIM }}>Nothing here.</span>
        ) : (
          <ModuleDetail
            h={h}
            euros={props.profile.euros}
            counts={counts}
            stash={props.profile.stash}
            moduleId={props.moduleId}
            nowMs={h.lastAdvanceMs}
          />
        )}
      </Panel>
    </Scrim>
  );
}

// ---------------------------------------------------------------------------
// The gym
// ---------------------------------------------------------------------------

export interface GymOverlayProps {
  profile: ProfileWire;
  /**
   * `performance.now()` when the profile landed. Optional, defaulting to
   * `nowMs` (fold nothing in) — a render test has no such instant.
   */
  profileAtMs?: number;
  /** A `performance.now()` reading, ticked once a second. */
  nowMs: number;
}

/**
 * THE BENCH PRESS: pick a skill, take the set, read the score. The gain is
 * capped by the SERVER (a score of zero still pays half a session, the
 * cooldown is stamped from its clock), so nothing here is worth cheating at.
 * The score is sent exactly once — the effect is keyed on the finished set and
 * guarded by a ref against StrictMode's double mount — and step three reads
 * `profile.progress.skills[skill]`, so the number moves when the server's
 * profile lands and not at all if the session was refused.
 */
export function GymOverlay(props: GymOverlayProps): ReactElement {
  const [skill, setSkill] = useState<SkillId | null>(null);
  const [game, setGame] = useState<GymState | null>(null);
  /** Whether this session's score has been sent. See the note above. */
  const sent = useRef(false);
  /**
   * The moving marker's node, written from a rAF loop rather than state (as
   * `MedCastBar` does).
   */
  const marker = useRef<HTMLDivElement | null>(null);

  const h = projectHideout(
    props.profile.hideout,
    props.profileAtMs ?? props.nowMs,
    props.nowMs,
  );
  const rows = trainingRows(h, props.profile.progress, h.lastAdvanceMs);
  const playing = game !== null && !game.done;

  const press = (): void => {
    setGame((g) => (g === null ? g : pressGym(g, performance.now())));
  };

  // The marker, while a set is running. Re-armed on every rep, which is ten
  // times a session and costs nothing.
  useEffect(() => {
    if (game === null || game.done) return;
    const startedAtMs = game.startedAtMs;
    let raf = 0;
    const step = (): void => {
      const el = marker.current;
      if (el !== null) {
        el.style.left = `${(markerPos01(performance.now() - startedAtMs) * 100).toFixed(2)}%`;
      }
      raf = requestAnimationFrame(step);
    };
    step();
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [game]);

  /**
   * Space (and Enter) is a rep. CAPTURE PHASE on `window`, swallowed, so the
   * body does not jump ten times. Escape is NOT handled — that is the game's.
   */
  useEffect(() => {
    if (!playing) return;
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.repeat) return;
      if (ev.code !== "Space" && ev.code !== "Enter" && ev.code !== "NumpadEnter") return;
      ev.preventDefault();
      ev.stopPropagation();
      press();
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
    };
  }, [playing]);

  // The set is over: tell the server, once.
  useEffect(() => {
    if (game === null || !game.done || skill === null || sent.current) return;
    sent.current = true;
    uiActions().hideoutTrain(skill, gymScore(game));
  }, [game, skill]);

  return (
    <Scrim>
      <Panel title="Kraftraum">
        {game === null ? (
          <SkillPick
            rows={rows}
            onPick={(picked) => {
              sent.current = false;
              setSkill(picked);
              setGame(startGym(performance.now()));
            }}
          />
        ) : game.done ? (
          <Result
            score01={gymScore(game)}
            label={rows.find((r) => r.skill === skill)?.label ?? "Skill"}
            value={skill === null ? 0 : (props.profile.progress.skills[skill] ?? 0)}
          />
        ) : (
          <GymSet game={game} markerRef={marker} onPress={press} />
        )}
      </Panel>
    </Scrim>
  );
}

/** Step one: which of the two, and can it be trained at all. */
function SkillPick(props: {
  rows: ReturnType<typeof trainingRows>;
  onPick: (skill: SkillId) => void;
}): ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <span style={{ fontSize: 11, color: TEXT_DIM, lineHeight: 1.45 }}>
        Ten reps. Hit the marker in the green and the set is worth the whole session.
      </span>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {props.rows.map((row) => {
          const why = row.problem === null ? null : reasonLabel(row.problem);
          return (
            <button
              key={row.skill}
              type="button"
              className="hidepick"
              disabled={row.problem !== null}
              title={why ?? `Train ${row.label}`}
              onClick={() => {
                props.onPick(row.skill);
              }}
            >
              <span style={{ fontSize: 14, letterSpacing: "0.04em" }}>{row.label}</span>
              <span style={{ fontSize: 20 }}>{row.value.toFixed(1)}</span>
              <span style={{ fontSize: 11, color: ACCENT }}>
                +{row.gainPerSession.toFixed(1)} per session
              </span>
              {why === null ? (
                row.cooldownLeftSec > 0 ? null : (
                  <span style={{ fontSize: 11, color: GOOD }}>Ready</span>
                )
              ) : (
                <span style={{ fontSize: 11, color: TEXT_DIM }}>
                  {row.cooldownLeftSec > 0
                    ? `${why} ${formatMmSs(row.cooldownLeftSec * 1000)}`
                    : why}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Step two: the bar, the zone, the marker, and the one thing to press. */
function GymSet(props: {
  game: GymState;
  markerRef: RefObject<HTMLDivElement | null>;
  onPress: () => void;
}): ReactElement {
  const { game } = props;
  const zone = zoneFor(game.rep);

  return (
    <button type="button" className="hidepress" onClick={props.onPress}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
        <span style={{ color: TEXT_DIM }}>
          Rep {game.rep + 1} / {GYM_REPS}
        </span>
        <span
          style={{
            color: game.lastResult === "hit" ? GOOD : game.lastResult === "miss" ? BAD : TEXT_DIM,
          }}
        >
          {game.lastResult === "hit" ? "Good" : game.lastResult === "miss" ? "Late" : " "}
        </span>
      </div>

      {/* The bar. The zone is a band painted at its own width, the marker a
          hairline moved by the loop above — neither is a React render. */}
      <div
        style={{
          position: "relative",
          height: 26,
          borderRadius: 3,
          border: `1px solid ${PANEL_LINE}`,
          background: "rgba(0, 0, 0, 0.48)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${(zone.lo * 100).toFixed(2)}%`,
            width: `${((zone.hi - zone.lo) * 100).toFixed(2)}%`,
            background: "rgba(143, 211, 154, 0.28)",
            borderLeft: "1px solid rgba(143, 211, 154, 0.6)",
            borderRight: "1px solid rgba(143, 211, 154, 0.6)",
          }}
        />
        <div
          ref={props.markerRef}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "0%",
            width: 2,
            marginLeft: -1,
            background: "#f4f8fc",
          }}
        />
      </div>

      <span style={{ fontSize: 11, color: TEXT_DIM }}>Space, or click — press</span>
    </button>
  );
}

/** Step three: what the set was worth, and where the skill stands now. */
function Result(props: { score01: number; label: string; value: number }): ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-start" }}>
      <span style={{ fontSize: 26, color: ACCENT }}>
        Score {Math.round(props.score01 * 100)} %
      </span>
      <span style={{ fontSize: 12 }}>
        {props.label} <span style={{ color: TEXT_DIM }}>now</span> {props.value.toFixed(1)}
      </span>
      <span style={{ fontSize: 11, color: TEXT_DIM, lineHeight: 1.45 }}>
        Session logged. The bench needs a while before the next one.
      </span>
      <button
        type="button"
        className="metabtn"
        onClick={() => {
          uiActions().closeHideoutOverlay();
        }}
      >
        Close
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The two boxes both overlays are drawn in
// ---------------------------------------------------------------------------

/**
 * The dimmed world behind a panel — lighter than the raid's Escape overlay,
 * because the room is what makes the panel make sense. `pointerEvents: "auto"`
 * stops clicks reaching the canvas.
 */
function Scrim(props: { children: ReactNode }): ReactElement {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(8, 9, 11, 0.55)",
        pointerEvents: "auto",
      }}
    >
      {props.children}
    </div>
  );
}

function Panel(props: { title: string; children: ReactNode }): ReactElement {
  return (
    <section
      className="hidepanel"
      style={{
        width: "100%",
        maxWidth: 560,
        maxHeight: "80vh",
        boxSizing: "border-box",
        margin: "0 16px",
        padding: "14px 16px 16px",
        borderRadius: 5,
        background: PANEL_BG,
        border: `1px solid ${PANEL_LINE}`,
        boxShadow: "0 16px 48px rgba(0, 0, 0, 0.6)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 0,
      }}
    >
      <header
        style={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 12,
          paddingBottom: 10,
          borderBottom: `1px solid ${PANEL_LINE}`,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, fontSize: 13, letterSpacing: "0.07em" }}>
          {props.title}
        </span>
        <button
          type="button"
          className="metabtn"
          aria-label="Close"
          onClick={() => {
            uiActions().closeHideoutOverlay();
          }}
        >
          ×
        </button>
      </header>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{props.children}</div>
    </section>
  );
}
